import { IGlobalComparatorOptions, ILoanQuote } from '@/app/models/loanQuote'
import { GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT, PROJECT_PURPOSE_RESIDENTIAL } from '@/app/constants/loanTypes'
import { getBorrowerSelectedOptions } from '@/app/utils/loan/borrower-mapper'

// Garanties exigées par la banque selon type de projet + statut emprunteur (parité Symfony requiredGuarantees).
export function getBankRequiredGuarantees(
    globalOptions: IGlobalComparatorOptions | null | undefined,
    bankName: string | null | undefined,
    projectPurpose: number,
    isOldBorrower = false
): string[] {
    const opts = globalOptions

    let requiredCodes: string[] =
        isOldBorrower && opts?.bankOldPeopleRequiredGuarantees
            ? opts.bankOldPeopleRequiredGuarantees
            : opts?.bankDefaultRequiredGuarantees || [GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT]

    if (bankName && opts) {
        const perInsuranceType = opts.bankRequiredGuaranteesPerInsuranceType?.[bankName]?.[projectPurpose]

        if (perInsuranceType) {
            requiredCodes = perInsuranceType
        } else if (projectPurpose !== PROJECT_PURPOSE_RESIDENTIAL) {
            requiredCodes = opts.bankNonResRequiredGuarantees || [GUARANTEE_DC, GUARANTEE_PTIA]
        } else if (bankName !== 'Autre' && bankName !== 'Je ne sais pas' && opts.bankRequiredGuarantees?.[bankName]) {
            requiredCodes = opts.bankRequiredGuarantees[bankName]
        }
    }

    return requiredCodes
}

// Le backend renvoie les codes en minuscules et 'ipt' pour IPT_ITT (parité Vue).
const toGuaranteeCode = (option: string): string => (option === 'ipt' ? GUARANTEE_IPT_ITT : option.toUpperCase())

/**
 * Les garanties retenues couvrent-elles celles que la banque exige ? Un assuré dont la sélection
 * manque une garantie exigée n'obtient pas un contrat équivalent, et la banque le refuserait.
 * Le co-emprunteur a sa propre sélection : elle compte dès qu'il est activé.
 */
export function hasBankEquivalentGuarantees(
    quote: ILoanQuote | null | undefined,
    globalOptions: IGlobalComparatorOptions | null | undefined
): boolean {
    if (!quote?.bank_name || !globalOptions) return true

    // Sur les résultats, project_purpose est absent (écrasé par la réponse backend) : type_insurance.id porte la valeur.
    const projectPurpose = quote.project_purpose ?? quote.type_insurance?.id ?? PROJECT_PURPOSE_RESIDENTIAL
    const bankRequired = getBankRequiredGuarantees(globalOptions, quote.bank_name, projectPurpose)
    if (bankRequired.length === 0) return true

    const covers = (borrower: Parameters<typeof getBorrowerSelectedOptions>[0]) => {
        const selected = getBorrowerSelectedOptions(borrower).map(toGuaranteeCode)
        return bankRequired.every((code) => selected.includes(code))
    }

    if (!covers(quote.main_borrower)) return false
    return !quote.co_borrower?.activated || covers(quote.co_borrower)
}
